import React, { useState, useEffect, useRef } from "react";

const CustomDatePicker = ({
  value,
  onChange,
  onBlur,
  name,
  min,
  max,
  placeholder = "DD MMM YYYY",
  isReadOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [showMonthDropdown, setShowMonthDropdown] = useState(false);
  const [showYearDropdown, setShowYearDropdown] = useState(false);
  const [viewMonth, setViewMonth] = useState(() => {
    if (value) return new Date(value);
    return new Date();
  });
  const pickerRef = useRef(null);

  const today = max ? new Date(max) : new Date();
  const minDate = min ? new Date(min) : null;
  const selectedDate = value ? new Date(value + "T00:00:00") : null;

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const dayNames = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

  const year = viewMonth.getFullYear();
  const month = viewMonth.getMonth();
  const firstDayOfMonth = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const prevMonth = () => setViewMonth(new Date(year, month - 1, 1));
  const nextMonth = () => setViewMonth(new Date(year, month + 1, 1));

  const handleDayClick = (day) => {
    if (isReadOnly) return;
    const d = new Date(year, month, day);
    if (d > today) return;
    if (minDate && d < minDate) return;
    const formatted = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
    onChange({ target: { name, value: formatted } });
    setIsOpen(false);
  };

  const isSelected = (day) => {
    if (!selectedDate) return false;
    return (
      selectedDate.getFullYear() === year &&
      selectedDate.getMonth() === month &&
      selectedDate.getDate() === day
    );
  };

  const isToday = (day) => {
    const t = new Date();
    return (
      t.getFullYear() === year && t.getMonth() === month && t.getDate() === day
    );
  };

  const isDisabled = (day) => {
    const d = new Date(year, month, day);
    if (d > today) return true;
    if (minDate && d < minDate) return true;
    return false;
  };

  const displayValue = selectedDate
    ? `${String(selectedDate.getDate()).padStart(2, "0")}/${String(
        selectedDate.getMonth() + 1
      ).padStart(2, "0")}/${selectedDate.getFullYear()}`
    : "";

  const yearRange = Array.from(
    { length: 101 },
    (_, i) => today.getFullYear() - i
  );

  return (
    <div className="relative" style={{maxWidth:"450px"}} ref={pickerRef}>
      <div
        className={`flex items-center justify-between bg-[#F9FAFB] border rounded-[4px] px-2 py-[3.5px] ${
          isReadOnly ? "cursor-not-allowed opacity-70" : "cursor-pointer"
        } select-none ${isOpen ? "border-[#666666]" : "border-[#666666]"}`}
        style={{ borderWidth: "0.25px", height: "35px", boxSizing: "border-box" }}
        onClick={() => {
          if (!isReadOnly) setIsOpen((p) => !p);
        }}
        onBlur={onBlur}
      >
        <span
          className={`text-allfonts text-[13px] ${
            displayValue ? "text-fontColor text-[#111729]" : "text-gray-400"
          }`}
          style={{ paddingLeft: "2px" }}
        >
          {displayValue || placeholder}
        </span>
        <svg
          className="w-4 h-4 ml-2 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <rect x="3" y="4" width="18" height="18" rx="2" strokeWidth="1.5" />
          <path
            d="M16 2v4M8 2v4M3 10h18"
            strokeWidth="1.5"
            strokeLinecap="round"
          />
        </svg>
      </div>

      {isOpen && (
        <div
          className="absolute top-full left-0 mt-1 bg-white rounded-[4px] shadow-2xl z-[10] overflow-hidden"
          style={{ width: "280px", border: "0.5px solid #e5e7eb" }}
        >
          <div className="flex items-center justify-between px-4 py-3 bg-[#ffffff]">
            <button
              type="button"
              onClick={prevMonth}
              className="text-[#111729] transition-colors p-1 rounded"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
            </button>
            <div className="flex items-center gap-4 relative">
              {/* Month Dropdown */}
              <div className="relative">
                <div
                  className="text-[#111729] text-sm font-medium cursor-pointer"
                  onClick={() => {
                    setShowMonthDropdown(!showMonthDropdown);
                    setShowYearDropdown(false);
                  }}
                >
                  {monthNames[month]}
                </div>
                {showMonthDropdown && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto z-50 min-w-[100px]">
                    {monthNames.map((m, i) => (
                      <div
                        key={m}
                        className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-sm text-black"
                        onClick={() => {
                          setViewMonth(new Date(year, i, 1));
                          setShowMonthDropdown(false);
                        }}
                      >
                        {m}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Year Dropdown */}
              <div className="relative">
                <div
                  className="text-[#111729] text-sm font-medium cursor-pointer"
                  onClick={() => {
                    setShowYearDropdown(!showYearDropdown);
                    setShowMonthDropdown(false);
                  }}
                >
                  {year}
                </div>
                {showYearDropdown && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-1 bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto z-50 min-w-[80px]">
                    {yearRange.map((y) => (
                      <div
                        key={y}
                        className="px-3 py-1 hover:bg-gray-100 cursor-pointer text-sm text-black"
                        onClick={() => {
                          setViewMonth(new Date(y, month, 1));
                          setShowYearDropdown(false);
                        }}
                      >
                        {y}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <button
              type="button"
              onClick={nextMonth}
              className="text-[#111729] transition-colors p-1 rounded"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5l7 7-7 7"
                />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 px-3 pt-3 pb-1">
            {dayNames.map((d) => (
              <div
                key={d}
                className="text-center text-xs font-semibold text-gray-400 py-1"
              >
                {d}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-7 px-3 pb-3 gap-y-1">
            {Array.from({ length: firstDayOfMonth }).map((_, i) => (
              <div key={`empty-${i}`} />
            ))}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map((day) => (
              <button
                key={day}
                type="button"
                disabled={isDisabled(day)}
                onClick={() => handleDayClick(day)}
                className={`
                  w-8 h-8 mx-auto flex items-center justify-center text-xs rounded-full
                  transition-all duration-150 font-medium
                  ${
                    isDisabled(day)
                      ? "text-gray-300 cursor-not-allowed"
                      : "cursor-pointer hover:bg-[#111729] hover:text-white"
                  }
                  ${isSelected(day) ? "bg-[#111729] text-white shadow-md" : ""}
                  ${
                    isToday(day) && !isSelected(day)
                      ? "border text-[#111729] font-bold"
                      : ""
                  }
                `}
                style={
                  isToday(day) && !isSelected(day)
                    ? { borderWidth: "0.5px", borderColor: "#111729" }
                    : {}
                }
              >
                {day}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomDatePicker;